import React, { useState, useEffect } from 'react';
import { GearIcon } from '@common_components/icons/gear.icon';
import { DisconnectIcon } from '@common_components/icons/disconnect.icon';
import LeftBar from '@common_components/layout/leftBar.component';
import { BookIcon, CommonAuthService, CommonGlobalService, FileIcon, FlashMeetingIcon, RootState, usePersistantState, UUID } from '@umanforge/common';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalService } from 'services/global.service';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import LeftBarPutTop from './leftBarPutTop.component';
import { useTranslation } from 'react-i18next';
import { KEY } from 'constants/local_storage_key.constant';

interface LeftBarProps {
  variant: 'basic' | 'table';
  hide?: boolean;
  barPosition?: 'auto' | 'left' | 'top';
  needConfirmation?: boolean;
}

const LeftBarManager: React.FC<LeftBarProps> = ( { variant, hide = false, barPosition = 'left', needConfirmation = false } ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ selectedClientId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );
  const [ selectedSiteId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SITE_ID, undefined );
  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ openDeleteModal, setOpenDeleteModal ] = useState( false );
  const [ openNavConfirmModal, setOpenNavConfirmModal ] = useState<boolean>( false );
  const [ pendingNavLink, setPendingNavLink ] = useState<string | null>( null );
  const [ windowWidth, setWindowWidth ] = useState( window.innerWidth );
  const [ windowHeight, setWindowHeight ] = useState( window.innerHeight );

  const [ isAdmin, setIsAdmin ] = useState<boolean>( false );

  useEffect( () => {
    setIsAdmin(
      me != undefined &&
      (
        ( selectedSectorId != undefined && CommonGlobalService.isAdminOfSector( me, selectedSectorId ) ) ||
        ( selectedSiteId != undefined && CommonGlobalService.isAdminOfSite( me, selectedSiteId ) ) ||
        ( selectedClientId != undefined && CommonGlobalService.isAdminOfClient( me, selectedClientId ) )
      )
    );
  }, [ me, selectedSectorId, selectedSiteId, selectedClientId ] );

  useEffect( () => {
    if ( barPosition === 'auto' && variant === 'table' ) {
      const handleResize = () => {
        setWindowWidth( window.innerWidth );
        setWindowHeight( window.innerHeight );
      };

      window.addEventListener( 'resize', handleResize );
      return () => {
        window.removeEventListener( 'resize', handleResize );
      };
    }
  }, [ barPosition, variant ] );

  const getEffectiveBarPosition = (): 'left' | 'top' => {
    if ( barPosition === 'auto' && variant === 'table' ) {
      return windowWidth > windowHeight ? 'left' : 'top';
    }
    if ( barPosition === 'auto' ) {
      return 'left';
    }
    return barPosition as 'left' | 'top';
  };

  const effectiveBarPosition = getEffectiveBarPosition();

  const handleConfirmDisconnect = async () => {
    setOpenDeleteModal( false );
    await CommonAuthService.signOutUser(
      token,
      dispatch,
      GlobalService.onAxiosWarning,
      err => {
        GlobalService.onAxiosError( err, dispatch );
      }
    );
  };

  const onDisconnect = () => {
    setOpenDeleteModal( true );
  };

  const baseNav = [
    {
      text: t( 'common:home' ),
      logo: <FlashMeetingIcon className="h-9 w-9 rounded-md" />,
      onClick: () => { return handleRequestedNavigation( '/Home' ); },
    },
    {
      text: t( 'common:extraction' ),
      logo: <FileIcon className="h-7 w-7 text-orange-base" />,
      onClick: () => { return handleRequestedNavigation( '/Extraction' ); },
    },
    {
      text: t( 'common:website_diary' ),
      logo: <BookIcon className="h-7 w-7 text-orange-base" />,
      onClick: () => { return handleRequestedNavigation( '/DiaryNote' ); },
    },
  ];

  const parametersNav = {
    text: t( 'common:parameters' ),
    logo: <GearIcon className="h-7 w-7 text-orange-base" />,
    onClick: () => { return handleRequestedNavigation( '/Parameters' ); },
  };

  const disconnectNav = {
    text: t( 'common:disconnect' ),
    logo: <DisconnectIcon className="h-7 w-7 text-orange-base" />,
    onClick: onDisconnect,
  };

  const items = [
    ...baseNav,
    ( isAdmin ) ? parametersNav : null,
    disconnectNav,
  ].filter( ( item ): item is Exclude<typeof item, null> => { return item !== null; } );

  const handleRequestedNavigation = ( link: string ) => {
    if ( typeof link !== 'string' ) { return; }
    // Dispatch global navigation attempt event so views (like Settings) can intercept
    interface NavigateAttemptDetail { path: string; handled?: boolean }
    const evtDetail: NavigateAttemptDetail = { path: link, handled: false };
    const evt = new CustomEvent<NavigateAttemptDetail>( 'app:navigateAttempt', { detail: evtDetail } );
    window.dispatchEvent( evt );
    // If a view handled the navigation (e.g., opened its own confirmation modal), do nothing
    if ( evtDetail && evtDetail.handled === true ) {
      return;
    }
    if ( needConfirmation === true ) {
      setPendingNavLink( link );
      setOpenNavConfirmModal( true );
    } else {
      navigate( link );
    }
  };

  const handleConfirmNav = () => {
    if ( pendingNavLink ) {
      navigate( pendingNavLink );
    }
    setPendingNavLink( null );
    setOpenNavConfirmModal( false );
  };

  const handleCancelNav = () => {
    setPendingNavLink( null );
    setOpenNavConfirmModal( false );
  };

  return (
    <>
      {
        effectiveBarPosition === 'left' ?
          <LeftBar hide={hide} items={items} />
          :
          <LeftBarPutTop hide={hide} items={items}/>
      }
      <ConfirmationModal
        isOpen={openDeleteModal}
        onConfirm={handleConfirmDisconnect}
        onCancel={() => {return setOpenDeleteModal( false );}}
        title={t( 'common:disconnect' )}
        description={t( 'common:are_you_sure_you_want_to_logout' )}
      />
      <ConfirmationModal
        isOpen={openNavConfirmModal}
        onConfirm={handleConfirmNav}
        onCancel={handleCancelNav}
        title={t( 'common:confirm_navigation' )}
        description={t( 'common:are_you_sure_you_want_to_leave' )}
      />
    </>
  );
};

export default LeftBarManager;